/** @jsxImportSource solid-js */
import CorvuDialog from '@corvu/dialog'
import { batch, createSignal, onCleanup, Show, type JSX } from 'solid-js'
import { Button } from './button'
import { cn } from '../cn'
import { DialogShell } from './dialog-shell'
import { Spinner } from './spinner'

export interface DialogData {
  /**
   * The dialog's heading. Omit it for a dialog that is only its
   * description — an explainer with nothing to announce — and no label
   * renders, so the panel is described by its text rather than labelled by
   * an empty heading.
   */
  title?: string
  description: string
  button: JSX.Element
  buttonTestId?: string
  action?: () => void
  /**
   * Async primary action. The dialog manages the loading state itself and
   * locks dismissal while the promise is in flight; it does NOT close when
   * the promise settles — end the flow with `dialog.close()`, replace the
   * payload with `dialog.replace()`, or queue a follow-up with
   * `dialog.closeThenOpen()`. Handle errors inside the action.
   */
  asyncAction?: () => Promise<void>
  /**
   * How the buttons sit. `horizontal` (default) puts the secondary button
   * beside the primary, secondary first; `vertical` stacks the primary above
   * the secondary at full width.
   */
  buttonLayout?: 'horizontal' | 'vertical'
  closeOnEscapeKeyDown?: boolean
  closeButton?: boolean | { action: () => void }
  overlayContent?: JSX.Element
  role?: 'dialog' | 'alertdialog'
  secondaryButton?: {
    action: () => void
    button: JSX.Element
    testId?: string
  }
  testId?: string
}

interface State {
  isOpen: boolean
  isLoading: boolean
  restoreFocus: boolean
  dialog: DialogData
}

interface DialogRequest {
  dialog: DialogData
  promise: Promise<void>
  resolve: () => void
}

const initialState: State = {
  isOpen: false,
  isLoading: false,
  restoreFocus: true,
  dialog: {
    description: '',
    button: '',
    closeButton: false,
  },
}

// Module-level signal (not exported): state transitions are whole-snapshot
// replacements, mirroring the React entry's nanostores atom.
const [state, setState] = createSignal<State>(initialState)
// Corvu content presence, exit animation included. A signal so `showing`
// can report it.
const [contentPresent, setContentPresent] = createSignal(false)
let activeDialog: DialogRequest | undefined
const pendingDialogs: DialogRequest[] = []

const setLoading = (isLoading: boolean) => {
  setState((prev) => ({ ...prev, isLoading }))
}

const createDialogRequest = (payload: DialogData): DialogRequest => {
  let resolve!: () => void
  const promise = new Promise<void>((nextResolve) => {
    resolve = nextResolve
  })
  return { dialog: payload, promise, resolve }
}

const showDialog = (request: DialogRequest) => {
  activeDialog = request
  setState({
    dialog: request.dialog,
    isOpen: true,
    isLoading: false,
    restoreFocus: true,
  })
}

const replace = (payload: DialogData) => {
  const request = createDialogRequest(payload)
  activeDialog?.resolve()
  showDialog(request)
  return request.promise
}

const completeActiveDialog = () => {
  activeDialog?.resolve()
  activeDialog = undefined
  const nextDialog = pendingDialogs.shift()
  if (nextDialog) showDialog(nextDialog)
}

const resetDialogManager = () => {
  activeDialog?.resolve()
  for (const request of pendingDialogs) request.resolve()
  activeDialog = undefined
  pendingDialogs.length = 0
  batch(() => {
    setContentPresent(false)
    setState(initialState)
  })
}

const handleContentPresentChange = (present: boolean) => {
  // Batched so a queued follow-up shows in the same update the outgoing
  // dialog's presence ends in: `showing` never reads a gap between them.
  batch(() => {
    setContentPresent(present)
    if (!present) completeActiveDialog()
  })
}

const close = (options?: { restoreFocus?: boolean }) => {
  const wasPresent = contentPresent()
  batch(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      isLoading: false,
      restoreFocus: options?.restoreFocus ?? true,
    }))
    if (!wasPresent) completeActiveDialog()
  })
}

const closeThenOpen = (payload: DialogData) => {
  const request = createDialogRequest(payload)
  pendingDialogs.push(request)
  if (!state().isOpen && !contentPresent()) {
    completeActiveDialog()
  } else if (state().isOpen) {
    close({ restoreFocus: false })
  } else {
    // `close(); open(next)` discovers the handoff after the exit began.
    // Update Corvu's live option before presence ends so focus moves into
    // the queued dialog instead of flashing back to the background.
    setState((prev) => ({ ...prev, restoreFocus: false }))
  }
  return request.promise
}

// `open` is the safe default: an existing or exiting dialog completes its
// presence before this request is shown. Call `replace` only when changing
// content inside the current shell is intentional.
const open = (payload: DialogData) => {
  if (state().isOpen || contentPresent()) return closeThenOpen(payload)
  return replace(payload)
}

// Reactive: true from the moment a dialog opens until the last one —
// queued follow-ups included — has finished its exit. Lets a composer hold
// its own dialog back so dialogs never stack.
const showing = () => state().isOpen || contentPresent()

export const dialog = {
  open,
  replace,
  closeThenOpen,
  close,
  showing,
}

export function Dialog() {
  onCleanup(resetDialogManager)

  const handleButtonClick = () => {
    const { action, asyncAction } = state().dialog
    if (asyncAction) {
      const request = activeDialog
      setLoading(true)
      asyncAction()
        .finally(() => {
          if (activeDialog === request) setLoading(false)
        })
        // Errors are the action's responsibility (see DialogData.asyncAction);
        // this is a safety net so a slipped rejection stays visible without
        // surfacing as an unhandled rejection in every consuming app.
        .catch((error: unknown) => console.error(error))
    } else {
      action?.()
      dialog.close()
    }
  }

  const handleSecondaryButtonClick = () => {
    state().dialog.secondaryButton?.action()
    dialog.close()
  }

  const buttonLayout = () => state().dialog.buttonLayout ?? 'horizontal'
  const horizontal = () => buttonLayout() === 'horizontal'
  // Side by side, each button gives up the size variant's full width for an
  // equal share of the row; stacked, they keep it.
  const widthClass = () => (horizontal() ? 'w-auto flex-1' : undefined)

  // Rendered on whichever side of the primary the layout puts it: DOM order
  // is reading and focus order, so the stacked layout puts the primary first
  // rather than reversing a secondary-first row visually.
  const SecondaryButton = () => (
    <Show when={state().dialog.secondaryButton}>
      {(secondary) => (
        <Button
          variant="ghost"
          class={widthClass()}
          data-testid={secondary().testId}
          disabled={state().isLoading || !state().isOpen}
          onClick={handleSecondaryButtonClick}
        >
          {secondary().button}
        </Button>
      )}
    </Show>
  )

  return (
    <DialogShell
      open={state().isOpen}
      role={state().dialog.role}
      closeOnEscapeKeyDown={state().dialog.closeOnEscapeKeyDown}
      restoreFocus={state().restoreFocus}
      onContentPresentChange={handleContentPresentChange}
      overlayContent={state().dialog.overlayContent}
      testId={state().dialog.testId}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen && !state().isLoading) dialog.close()
      }}
      class="data-[open]:animate-in data-[closed]:animate-out data-[open]:fade-in-0 data-[closed]:fade-out-0 data-[open]:zoom-in-95 data-[closed]:zoom-out-95 w-[calc(100vw-48px)] max-w-[300px] ease-out"
      overlayClass="ease-out data-[open]:animate-in data-[closed]:animate-out data-[open]:fade-in-0 data-[closed]:fade-out-0"
    >
      <div class="p-6 text-center">
        <Show when={state().dialog.title}>
          {(title) => (
            <CorvuDialog.Label class="text-lg font-semibold text-[#00524F]">
              {title()}
            </CorvuDialog.Label>
          )}
        </Show>

        <CorvuDialog.Description
          class={cn(
            'whitespace-pre-line text-[#00524F]/75',
            state().dialog.title && 'mt-3',
          )}
        >
          {state().dialog.description}
        </CorvuDialog.Description>

        <div
          data-button-layout={buttonLayout()}
          class={cn(
            'mt-4 flex gap-3',
            horizontal() ? 'items-center' : 'flex-col items-stretch',
          )}
        >
          <Show when={horizontal()}>
            <SecondaryButton />
          </Show>
          <Button
            class={cn(
              'relative flex items-center justify-center',
              widthClass(),
            )}
            data-testid={state().dialog.buttonTestId}
            disabled={state().isLoading || !state().isOpen}
            onClick={handleButtonClick}
          >
            <Show when={state().isLoading}>
              <Spinner class="absolute right-7 size-4 text-white" />
            </Show>
            {state().dialog.button}
          </Button>
          <Show when={!horizontal()}>
            <SecondaryButton />
          </Show>
        </div>
      </div>

      <Show
        when={
          state().dialog.closeButton && !state().isLoading && state().isOpen
        }
      >
        <button
          class="absolute top-[-10px] right-[-10px] grid size-[42px] place-items-center overflow-hidden rounded-full bg-[rgba(255,255,255,0.56)] backdrop-blur-[37.5px]"
          onClick={() => {
            const closeButton = state().dialog.closeButton
            if (typeof closeButton === 'object') closeButton.action()
            dialog.close()
          }}
        >
          <CloseSvg />
        </button>
      </Show>
    </DialogShell>
  )
}

function CloseSvg() {
  return (
    <svg
      width="19"
      height="18"
      viewBox="0 0 19 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.21429 4.71442L13.7857 13.2858"
        stroke="#333333"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.21427 13.2858L13.7856 4.71442"
        stroke="#333333"
        stroke-width="1.3"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  )
}
